import { useCallback, useEffect, useState } from 'react';
import { useMultisigStore } from '@/stores/multisig';
import { signedFetch } from '@/lib/signedFetch';

interface SessionRow {
  tokenSuffix: string;
  expiresAt: string;
  createdAt: string;
}

function shortAddr(addr: string) {
  return addr.slice(0, 6) + '…' + addr.slice(-6);
}

export default function Settings() {
  const activePda = useMultisigStore((s) => s.activePda);

  const [sessions, setSessions] = useState<SessionRow[]>([]);
  const [loadingSessions, setLoadingSessions] = useState(false);
  const [sessionsError, setSessionsError] = useState<string | null>(null);

  const [showRegen, setShowRegen] = useState(false);
  const [regenCode, setRegenCode] = useState('');
  const [regenBusy, setRegenBusy] = useState(false);
  const [regenError, setRegenError] = useState<string | null>(null);
  const [newRecoveryCodes, setNewRecoveryCodes] = useState<string[]>([]);

  const loadSessions = useCallback(async () => {
    if (!activePda) return;
    setLoadingSessions(true);
    setSessionsError(null);
    try {
      const res = await signedFetch<{ sessions: SessionRow[] }>('session.list');
      if (!res.ok) throw new Error(`load failed: ${res.status}`);
      setSessions(res.data.sessions);
    } catch (err) {
      setSessionsError((err as Error).message);
    } finally {
      setLoadingSessions(false);
    }
  }, [activePda]);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  async function regenerateRecovery() {
    if (!/^\d{6}$/.test(regenCode)) {
      setRegenError('enter a 6-digit code');
      return;
    }
    setRegenBusy(true);
    setRegenError(null);
    try {
      const res = await signedFetch<{ recoveryCodes: string[] }>(
        'mfa.totp.recovery.regenerate',
        { fields: { Code: regenCode } },
      );
      if (!res.ok) throw new Error(`failed: ${res.status}`);
      setNewRecoveryCodes(res.data.recoveryCodes);
      setRegenCode('');
    } catch (err) {
      setRegenError((err as Error).message);
    } finally {
      setRegenBusy(false);
    }
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="font-display text-2xl font-semibold">Settings</h1>

      <section className="rounded-xl border border-ink-800 bg-ink-900/40 p-5 space-y-3">
        <h2 className="font-semibold text-ink-100">Multisig</h2>
        <div className="text-sm space-y-1">
          <div>
            <span className="text-ink-400 mr-2">Address:</span>
            <span className="font-mono text-ink-100">
              {activePda ? shortAddr(activePda) : '—'}
            </span>
          </div>
        </div>
      </section>

      <section className="rounded-xl border border-ink-800 bg-ink-900/40 p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold text-ink-100">Active sessions</h2>
          <button
            type="button"
            onClick={() => void loadSessions()}
            disabled={loadingSessions}
            className="rounded-lg border border-ink-800 bg-ink-900/60 px-3 py-1.5 text-xs text-ink-100 hover:bg-ink-800/60 transition-colors"
          >
            {loadingSessions ? '…' : 'Refresh'}
          </button>
        </div>
        {sessionsError && <p className="text-sm text-red-400">{sessionsError}</p>}
        {!sessionsError && sessions.length === 0 ? (
          <div className="text-sm text-ink-400">No active sessions.</div>
        ) : (
          <ul className="space-y-2">
            {sessions.map((s) => (
              <li
                key={s.tokenSuffix}
                className="rounded-lg border border-ink-800 bg-ink-900 px-4 py-2 text-sm flex items-center justify-between"
              >
                <span className="font-mono text-ink-200">…{s.tokenSuffix}</span>
                <span className="text-xs text-ink-400">
                  expires {new Date(s.expiresAt).toLocaleString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="rounded-xl border border-ink-800 bg-ink-900/40 p-5 space-y-3">
        <h2 className="font-semibold text-ink-100">Recovery codes</h2>
        <p className="text-sm text-ink-300">
          Regenerate your 8 recovery codes. Old codes are invalidated.
        </p>
        {!showRegen ? (
          <div>
            <button
              type="button"
              onClick={() => setShowRegen(true)}
              className="rounded-lg border border-ink-800 bg-ink-900/60 px-4 py-2 text-sm text-ink-100 hover:bg-ink-800/60 transition-colors"
            >
              Regenerate codes
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <input
              value={regenCode}
              onChange={(e) => setRegenCode(e.target.value)}
              inputMode="numeric"
              maxLength={6}
              placeholder="6-digit TOTP"
              className="w-full rounded-lg border border-ink-800 bg-ink-900 px-4 py-2.5 text-center font-mono text-lg tracking-widest text-ink-50 focus:border-avo-500 focus:outline-none"
            />
            {regenError && <p className="text-sm text-red-400">{regenError}</p>}
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  setShowRegen(false);
                  setRegenError(null);
                }}
                className="flex-1 rounded-lg border border-ink-800 bg-ink-900/60 px-4 py-2 text-sm text-ink-100 hover:bg-ink-800/60 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void regenerateRecovery()}
                disabled={regenBusy}
                className="flex-1 rounded-lg bg-avo-500 px-4 py-2 text-sm font-medium text-ink-950 hover:bg-avo-400 transition-colors disabled:opacity-50"
              >
                {regenBusy ? 'Regenerating…' : 'Confirm'}
              </button>
            </div>
          </div>
        )}
        {newRecoveryCodes.length > 0 && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-4 mt-3">
            <p className="text-sm text-amber-200 mb-2">
              <strong>Save these. Old codes are invalid.</strong>
            </p>
            <div className="grid grid-cols-2 gap-2 font-mono text-xs">
              {newRecoveryCodes.map((c, i) => (
                <div
                  key={i}
                  className="rounded bg-ink-950/60 px-2 py-1.5 text-ink-100"
                >
                  {String(i + 1).padStart(2, '0')}. {c}
                </div>
              ))}
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
